import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Plus, X, Sparkles, Loader2, Users } from "lucide-react";
import CreditCostTag from "../../CreditCostTag";
import { DefineAiDisclosure } from "./DefineAiDisclosure";

function getStakeholderQuadrant(influence, interest) {
  if (influence === "high" && interest === "high") {
    return {
      label: "Manage Closely",
      description: "Core decision-makers and delivery owners.",
    };
  }
  if (influence === "high" && interest === "low") {
    return {
      label: "Manage Carefully",
      description: "High influence + low interest stakeholders need selective, deliberate updates.",
    };
  }
  if (influence === "low" && interest === "high") {
    return {
      label: "Key Allies",
      description: "Low influence + high interest stakeholders are often frontline allies and change adopters.",
    };
  }
  if (influence === "medium" && interest === "high") {
    return {
      label: "Keep Engaged",
      description: "Operational stakeholders with strong interest can become adoption leaders and practical sounding boards.",
    };
  }
  if (influence === "high" && interest === "medium") {
    return {
      label: "Keep Aligned",
      description: "High-influence stakeholders with moderate day-to-day interest still need targeted decisions and escalation context.",
    };
  }
  return {
    label: "Monitor",
    description: "Keep informed without over-engineering the engagement plan.",
  };
}

export function StakeholdersTab({
  stakeholders,
  saving,
  aiGenerating,
  hasProblemText,
  aiDraftActive,
  addStakeholder,
  updateStakeholder,
  removeStakeholder,
  saveStakeholders,
  applyStakeholderTemplate,
  generateStakeholderSuggestion,
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="font-heading font-semibold">Stakeholder Map</h3>
          <p className="text-xs text-muted-foreground" data-testid="stakeholder-guidance">
            Start with a realistic governance mix, then edit names, roles, influence, and interest to fit the project. Draft suggestions do not auto-save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={applyStakeholderTemplate} data-testid="stakeholder-template-btn">
            <Users className="w-4 h-4 mr-1" />
            Load LSS Template
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={generateStakeholderSuggestion}
            disabled={!!aiGenerating || !hasProblemText}
            className="gap-2"
            data-testid="ai-suggest-stakeholders-btn"
          >
            {aiGenerating === "Stakeholder Draft" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Suggest Stakeholders
            <CreditCostTag cost={2} />
          </Button>
          <Button variant="outline" size="sm" onClick={addStakeholder} data-testid="add-stakeholder-btn">
            <Plus className="w-4 h-4 mr-1" />
            Add Stakeholder
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background/50 p-4 space-y-2" data-testid="stakeholder-role-guidance">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Role Guidance</p>
        <p className="text-xs text-muted-foreground">
          Typical Define coverage includes Sponsor / Champion, Process Owner, Quality Lead, Project Lead / Black Belt, Frontline Operators, and downstream voices such as Supply Chain or Voice of Customer when they are affected.
        </p>
        <p className="text-xs text-muted-foreground">
          Keep influence and interest realistic. Governance roles are often high influence, while operational teams may be medium influence and high interest.
        </p>
      </div>

      {aiDraftActive && (
        <DefineAiDisclosure
          title="AI-generated stakeholder draft"
          detail="These stakeholder roles and influence/interest defaults were suggested from Define context. Review names, governance assumptions, and engagement levels before saving."
          testId="stakeholder-ai-disclosure"
        />
      )}

      <div className="rounded-lg border border-border bg-background/50 p-4 space-y-3" data-testid="stakeholder-governance-guidance">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Influence / Interest Guide</p>
        <div className="grid gap-2 md:grid-cols-2 text-xs text-muted-foreground">
          <div className="rounded-md border border-border px-3 py-2">
            <p className="font-medium text-foreground">High influence + high interest</p>
            <p>Manage closely. These are the core governance and project-delivery stakeholders.</p>
          </div>
          <div className="rounded-md border border-border px-3 py-2">
            <p className="font-medium text-foreground">High influence + low interest</p>
            <p>Monitor and manage carefully. They can unblock or constrain the project even if it is not their daily priority.</p>
          </div>
          <div className="rounded-md border border-border px-3 py-2">
            <p className="font-medium text-foreground">Low influence + high interest</p>
            <p>Key frontline allies. They often see the pain directly and help with adoption, validation, and change pull-through.</p>
          </div>
          <div className="rounded-md border border-border px-3 py-2">
            <p className="font-medium text-foreground">Low influence + low interest</p>
            <p>Monitor. Keep them informed at the right level without overcomplicating the engagement plan.</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {stakeholders.map((stakeholder, index) => {
          const quadrant = getStakeholderQuadrant(stakeholder.influence, stakeholder.interest);
          return (
          <div
            key={index}
            className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-background border border-border rounded-lg"
          >
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input
                value={stakeholder.name}
                onChange={(e) => updateStakeholder(index, "name", e.target.value)}
                placeholder="Stakeholder name"
                data-testid={`stakeholder-name-${index}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Role</Label>
              <Input
                value={stakeholder.role}
                onChange={(e) => updateStakeholder(index, "role", e.target.value)}
                placeholder="Role/Title"
                data-testid={`stakeholder-role-${index}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Influence</Label>
              <select
                value={stakeholder.influence}
                onChange={(e) => updateStakeholder(index, "influence", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid={`stakeholder-influence-${index}`}
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Interest</Label>
              <select
                value={stakeholder.interest}
                onChange={(e) => updateStakeholder(index, "interest", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid={`stakeholder-interest-${index}`}
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="flex items-end">
              {stakeholders.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeStakeholder(index)}
                  className="text-destructive"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="md:col-span-5 rounded-md border border-border/70 bg-card/60 px-3 py-2" data-testid={`stakeholder-quadrant-${index}`}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{quadrant.label}</p>
              <p className="text-xs text-muted-foreground">{quadrant.description}</p>
            </div>
          </div>
        )})}
      </div>

      <Button onClick={saveStakeholders} disabled={saving} data-testid="save-stakeholders-btn">
        {saving ? "Saving..." : "Save Stakeholders"}
      </Button>
    </div>
  );
}
